/* assets/product-showcase.js */
(function () {
  'use strict';

  const CART_ID_RE = /header|cart/i;
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartAddUrl = () => (window.routes && window.routes.cart_add_url) || routeRoot() + 'cart/add';

  class ProductShowcase extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ps-track]');
      this.empty = this.querySelector('[data-ps-empty]');
      this.prevBtn = this.querySelector('[data-ps-prev]');
      this.nextBtn = this.querySelector('[data-ps-next]');
      this.cards = Array.from(this.querySelectorAll('[data-ps-card]'));
      this.filters = Array.from(this.querySelectorAll('[data-ps-filter]'));
      this.isSlider = this.dataset.layout === 'slider';

      this.classList.add('ps--js');

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('visibilitychange', this.onVisibility);

      if (this.track) {
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
      }

      /* Each init step is isolated. A single throw used to abort the rest of
         connectedCallback, leaving `ps--js` applied but `is-inview` never
         added — the whole section rendered and then vanished. */
      this.safe(() => this.setStagger());
      this.safe(() => { if (window.DvyWishlist) window.DvyWishlist.sync(this); });
      this.safe(() => this.initDrag());
      this.safe(() => this.initReveal());
      this.safe(() => this.initAutoplay());
      this.safe(() => this.onScroll());
    }

    safe(fn) {
      try { fn(); } catch (e) { console.error('[product-showcase]', e); }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.observer) this.observer.disconnect();
      if (this.playObserver) this.playObserver.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.revealTimer) clearTimeout(this.revealTimer);
      if (this.teardownDrag) this.teardownDrag();
      this.stopAutoplay();
    }

    /* ---------- Reveal ---------- */
    setStagger() {
      this.cards.forEach((card, i) => {
        card.style.setProperty('--ps-delay', Math.min(i, 6) * 70 + 'ms');
      });
    }

    initReveal() {
      if (this.dataset.animate !== 'true' || REDUCED.matches || !('IntersectionObserver' in window)) {
        this.classList.add('is-inview');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.classList.add('is-inview');
            this.observer.disconnect();
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
      );

      this.observer.observe(this);

      /* Safety net: if the observer never fires (zero-height parent, lazy
         container, offscreen at load) force the reveal. Permanently
         invisible content is worse than an unanimated entrance. */
      this.revealTimer = setTimeout(() => this.classList.add('is-inview'), 2000);
    }

    /* ---------- Autoplay ---------- */
    autoplayAllowed() {
      if (!this.isSlider || !this.track) return false;
      if (this.dataset.autoplay !== 'true') return false;
      if (REDUCED.matches) return false;

      const desktop = window.matchMedia('(min-width: 750px)').matches;
      if (desktop && this.dataset.autoplayDesktop !== 'true') return false;

      return true;
    }

    initAutoplay() {
      if (!this.autoplayAllowed()) return;

      const seconds = parseInt(this.dataset.autoplaySpeed, 10) || 3;
      this.autoplayDelay = Math.max(2, seconds) * 1000;

      this.track.addEventListener('pointerdown', this.pause, { passive: true });
      this.track.addEventListener('touchstart', this.pause, { passive: true });
      this.track.addEventListener('touchend', this.resume, { passive: true });
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);

      if ('IntersectionObserver' in window) {
        this.playObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.startAutoplay();
              else this.stopAutoplay();
            });
          },
          { threshold: 0.2 }
        );
        this.playObserver.observe(this);
      }

      setTimeout(() => this.startAutoplay(), 800);
    }

    startAutoplay() {
      if (this.timer || !this.autoplayDelay || this.paused) return;
      this.timer = setInterval(() => this.advance(), this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    resume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.paused = false;
        this.startAutoplay();
      }, 2500);
    }

    onVisibility() {
      if (document.hidden) this.stopAutoplay();
      else if (!this.paused) this.startAutoplay();
    }

    advance() {
      if (!this.track) return;

      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 0) return;

      if (this.track.scrollLeft >= max - 2) {
        this.animateTo(0, 700);
        return;
      }

      this.step(1);
    }

    /* ---------- Slider nav ---------- */
    stepSize() {
      const card = this.cards.find((c) => !c.hidden);
      if (!card || !this.track) return this.track ? this.track.clientWidth : 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    animateTo(target, duration) {
      if (!this.track) return;

      const max = this.track.scrollWidth - this.track.clientWidth;
      const to = Math.max(0, Math.min(target, max));
      const from = this.track.scrollLeft;
      const delta = to - from;

      if (Math.abs(delta) < 1) return;
      if (this.raf) cancelAnimationFrame(this.raf);

      const ms = duration || 520;
      const t0 = performance.now();

      const tick = (now) => {
        const p = Math.min(1, (now - t0) / ms);
        const eased = 1 - Math.pow(1 - p, 3);
        this.track.scrollLeft = from + delta * eased;

        if (p < 1) this.raf = requestAnimationFrame(tick);
        else this.raf = null;
      };

      this.raf = requestAnimationFrame(tick);
    }

    step(direction) {
      if (!this.track) return;
      this.animateTo(this.track.scrollLeft + this.stepSize() * direction);
    }

    onScroll() {
      if (!this.track || !this.prevBtn || !this.nextBtn) return;

      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;

      this.prevBtn.disabled = x <= 1;
      this.nextBtn.disabled = x >= max - 1;
    }

    /* ---------- Events ---------- */
    onClick(event) {
      /* Wishlist is owned by DvyWishlist's capture-phase handler — no
         wish branch here, or the two would fight over the same click. */

      const filter = event.target.closest('[data-ps-filter]');
      if (filter && this.contains(filter)) return this.applyFilter(filter);

      const prev = event.target.closest('[data-ps-prev]');
      if (prev && this.contains(prev)) return this.step(-1);

      const next = event.target.closest('[data-ps-next]');
      if (next && this.contains(next)) return this.step(1);

      const chip = event.target.closest('[data-ps-variant]');
      if (chip && this.contains(chip)) {
        event.preventDefault();
        const card = chip.closest('[data-ps-card]');
        this.closePicker();
        return this.addToCart(card.querySelector('.ps__action--cart'), parseInt(chip.dataset.psVariant, 10));
      }

      const pick = event.target.closest('[data-ps-pick]');
      if (pick && this.contains(pick)) {
        event.preventDefault();
        return this.togglePicker(pick);
      }

      const goto = event.target.closest('[data-ps-goto]');
      if (goto && this.contains(goto)) {
        event.preventDefault();
        window.location.href = goto.dataset.url;
        return;
      }

      const add = event.target.closest('[data-ps-add]');
      if (add && this.contains(add)) {
        event.preventDefault();
        this.addToCart(add, parseInt(add.dataset.variantId, 10));
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closePicker();
      if (!this.isSlider) return;
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    }

    onDocClick(event) {
      if (!this.openPicker) return;
      if (event.target.closest('.ps__picker') || event.target.closest('[data-ps-pick]')) return;
      this.closePicker();
    }

    /* ---------- Variant picker ---------- */
    togglePicker(button) {
      const card = button.closest('[data-ps-card]');
      if (card.querySelector('.ps__picker')) return this.closePicker();

      this.closePicker();
      this.pause();

      const json = card.querySelector('[data-ps-variants]');
      if (!json) return;

      let data;
      try {
        data = JSON.parse(json.textContent);
      } catch (e) {
        console.error('[product-showcase] variant JSON parse failed', e);
        return;
      }

      const picker = document.createElement('div');
      picker.className = 'ps__picker';
      picker.setAttribute('role', 'group');
      picker.setAttribute('aria-label', 'Choose ' + (data.option || 'option'));

      const label = document.createElement('span');
      label.className = 'ps__picker-label';
      label.textContent = data.option || 'Select';
      picker.appendChild(label);

      const row = document.createElement('div');
      row.className = 'ps__picker-row';

      data.variants.forEach((variant) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'ps__chip';
        chip.textContent = variant.title;

        if (variant.available) chip.setAttribute('data-ps-variant', variant.id);
        else {
          chip.disabled = true;
          chip.setAttribute('aria-disabled', 'true');
        }

        row.appendChild(chip);
      });

      picker.appendChild(row);
      card.querySelector('.ps__media').appendChild(picker);
      this.openPicker = picker;

      const first = picker.querySelector('[data-ps-variant]');
      if (first) first.focus();
    }

    closePicker() {
      if (this.openPicker && this.openPicker.parentNode) this.openPicker.remove();
      this.openPicker = null;
    }

    /* ---------- Filtering ---------- */
    applyFilter(button) {
      const type = button.dataset.type || 'all';
      const tag = (button.dataset.tag || '').trim();
      const min = parseInt(button.dataset.min, 10) || 0;
      const max = parseInt(button.dataset.max, 10) || 0;

      this.closePicker();

      this.filters.forEach((btn) => {
        const active = btn === button;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (typeof button.scrollIntoView === 'function') {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      let visible = 0;

      this.cards.forEach((card) => {
        let match = true;

        if (type === 'tag') {
          match = tag !== '' && (card.dataset.tags || '').includes('|' + tag + '|');
        } else if (type === 'price') {
          const price = parseInt(card.dataset.price, 10) || 0;
          match = price >= min && (max <= 0 || price <= max);
        }

        card.hidden = !match;
        if (match) visible += 1;
      });

      if (this.empty) this.empty.hidden = visible !== 0;
      this.animateTo(0, 420);
      requestAnimationFrame(this.onScroll);
    }

    /* ---------- Cart ---------- */
    cartSectionIds() {
      return Array.from(document.querySelectorAll('[id^="shopify-section-"]'))
        .map((el) => el.id.replace('shopify-section-', ''))
        .filter((id) => CART_ID_RE.test(id))
        .slice(0, 5);
    }

    async addToCart(button, variantId) {
      if (!button || !variantId || button.classList.contains('is-loading')) return;

      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      this.pause();

      const sections = this.cartSectionIds();

      try {
        const response = await fetch(cartAddUrl() + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || 'Add to cart failed');

        if (data.sections) this.renderSections(data.sections);
        else await this.refreshCartCount();

        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1400);

        this.notifyCart();
      } catch (error) {
        console.error('[product-showcase]', error);
        button.classList.add('is-error');
        setTimeout(() => button.classList.remove('is-error'), 1800);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    renderSections(sections) {
      const parser = new DOMParser();

      Object.keys(sections).forEach((id) => {
        const target = document.getElementById('shopify-section-' + id);
        if (!target || typeof sections[id] !== 'string') return;

        const doc = parser.parseFromString(sections[id], 'text/html');
        const source = doc.getElementById('shopify-section-' + id) || doc.body;
        target.innerHTML = source.innerHTML;
      });

      /* Server-rendered markup has no wishlist state — reapply it. */
      if (window.DvyWishlist) window.DvyWishlist.sync(document);
    }

    async refreshCartCount() {
      try {
        const res = await fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;

        const cart = await res.json();
        const count = String(cart.item_count);

        document
          .querySelectorAll('[data-cart-count], .cart-count-bubble span:first-child, .cart-count, #cart-icon-bubble span:first-child')
          .forEach((el) => {
            el.textContent = count;
          });
      } catch (e) {
        /* non-fatal */
      }
    }

    notifyCart() {
      ['cart:refresh', 'cart:update', 'cart:change'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { source: 'product-showcase' } }));
      });

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'product-showcase' });
        } catch (e) {
          /* theme bus mismatch — the events above still fire */
        }
      }

      const drawer = document.querySelector('cart-drawer, [id*="cart-drawer" i]');
      if (drawer && typeof drawer.open === 'function') {
        try {
          drawer.open();
        } catch (e) {
          /* theme drawer API differs */
        }
      }
    }

    /* ---------- Desktop drag-to-scroll ---------- */
    initDrag() {
      if (!this.track || !this.isSlider) return;
      if (!window.matchMedia('(pointer: fine)').matches) return;

      const THRESHOLD = 4;

      let down = false;
      let captured = false;
      let pointerId = null;
      let startX = 0;
      let startLeft = 0;
      let moved = 0;

      const release = () => {
        if (!down) return;
        down = false;

        if (captured && pointerId !== null) {
          try {
            if (this.track.hasPointerCapture && this.track.hasPointerCapture(pointerId)) {
              this.track.releasePointerCapture(pointerId);
            }
          } catch (e) {
            /* pointer already gone */
          }
        }

        captured = false;
        pointerId = null;
        this.track.classList.remove('is-dragging');
      };

      const onDown = (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (e.target.closest('button')) return;

        down = true;
        captured = false;
        moved = 0;
        pointerId = e.pointerId;
        startX = e.clientX;
        startLeft = this.track.scrollLeft;

        /* Pointer capture is DELIBERATELY not taken here. Capturing on
           pointerdown retargets pointerup to the track, so the browser fires
           `click` on the common ancestor instead of the <a> — card links
           silently stop working. Capture happens in onMove once a real drag
           is confirmed. */
        if (this.raf) cancelAnimationFrame(this.raf);
      };

      const onMove = (e) => {
        if (!down) return;

        // Button released outside the window, or a drag swallowed the pointerup
        if (e.buttons === 0) {
          release();
          return;
        }

        const delta = e.clientX - startX;
        moved = Math.abs(delta);

        if (!captured) {
          if (moved < THRESHOLD) return; // still a click — leave the DOM alone
          captured = true;
          this.track.classList.add('is-dragging');
          try {
            this.track.setPointerCapture(pointerId);
          } catch (err) {
            /* capture unsupported — drag still works, just less robust */
          }
        }

        this.track.scrollLeft = startLeft - delta;
      };

      const onClickCapture = (e) => {
        if (moved > THRESHOLD + 2) {
          e.preventDefault();
          e.stopPropagation();
        }
        // Always reset, or a stale drag blocks the next genuine click
        moved = 0;
      };

      const onDragStart = (e) => e.preventDefault();

      this.track.addEventListener('pointerdown', onDown);
      this.track.addEventListener('pointermove', onMove);
      this.track.addEventListener('pointerup', release);
      this.track.addEventListener('pointercancel', release);
      this.track.addEventListener('lostpointercapture', release);
      this.track.addEventListener('dragstart', onDragStart);
      this.track.addEventListener('click', onClickCapture, true);
      window.addEventListener('blur', release);

      this.teardownDrag = () => {
        this.track.removeEventListener('pointerdown', onDown);
        this.track.removeEventListener('pointermove', onMove);
        this.track.removeEventListener('pointerup', release);
        this.track.removeEventListener('pointercancel', release);
        this.track.removeEventListener('lostpointercapture', release);
        this.track.removeEventListener('dragstart', onDragStart);
        this.track.removeEventListener('click', onClickCapture, true);
        window.removeEventListener('blur', release);
      };
    }
  }

  if (!customElements.get('product-showcase')) {
    customElements.define('product-showcase', ProductShowcase);
  }
})();